import { useEffect, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { isLocalhostSignIn } from '@/config/aws'
import Layout from '@/components/Layout'
import CallbackPage from '@/pages/CallbackPage'
import RulesPage from '@/pages/RulesPage'
import { ShieldOff } from 'lucide-react'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, isLoading, user, signIn, signOut } =
    useAuth()
  const autoSignInTriggered = useRef(false)

  useEffect(() => {
    if (isLoading || isAuthenticated || autoSignInTriggered.current) return
    autoSignInTriggered.current = true
    if (isLocalhostSignIn) {
      signIn()
    } else {
      const portalUrl = import.meta.env.VITE_PORTAL_URL
      if (portalUrl) {
        const returnTo = encodeURIComponent(window.location.href)
        window.location.href = `${portalUrl}/?return_to=${returnTo}`
      } else {
        signIn()
      }
    }
  }, [isLoading, isAuthenticated, signIn])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <p className="ml-4 text-gray-500">
          ログイン画面にリダイレクトしています...
        </p>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <ShieldOff className="h-16 w-16 text-red-400 mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          アクセス権限がありません
        </h1>
        <p className="text-gray-600 mb-1">
          このアプリはadminロールのユーザーのみ使用できます。
        </p>
        <p className="text-sm text-gray-500 mb-6">
          ログインユーザー: {user?.email}
        </p>
        <button
          onClick={signOut}
          className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
        >
          ログアウト
        </button>
      </div>
    )
  }

  return <>{children}</>
}

function App() {
  return (
    <Routes>
      <Route path="/callback" element={<CallbackPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RulesPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
